import { Component, OnInit, HostListener, ViewChild, DestroyRef } from '@angular/core';
import { ConfirmModalComponent } from '@shared/modals/confirm-modal/confirm-modal.component';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import { ModalService } from '@core/services/modal/modal.service';
import { CLASSES, Countries, SELECTORS, STORAGE_NAMES } from '@root/app.config';
import { Country } from '@root/app.interfaces';
import { MatSelect } from '@angular/material/select';
import { GtmService } from '@core/services/gtm/gtm.service';
import { GTM_COUNTRY_EVENS, GTM_HEADER_EVENTS } from '@core/services/gtm/gtm.events';
import { VariantService } from '@core/services/variant/variant.service';
import { ResetAppService } from '@core/services/reset-app/reset-app.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'app-country-selector',
	templateUrl: './country-selector.component.html',
	styleUrls: ['./country-selector.component.scss', './country-selector.responsive.scss'],
})
export class CountrySelectorComponent implements OnInit {
	@ViewChild('matSelect') matSelect: MatSelect;

	protected readonly countries = Countries;
	protected readonly isShop = this.variant.isShop();

	protected isOpen: boolean;
	protected selectedCountry: Country = null;

	@HostListener('window:click', ['$event']) onClickEvent(event: Event) {
		const target = event.target as HTMLElement;

		if (this.isOpen && !target.closest(SELECTORS.country_selector)) {
			this.matSelect.close();
		}
	}

	constructor(
		private gtm: GtmService,
		private destroyRef: DestroyRef,
		private variant: VariantService,
		private modalService: ModalService,
		private shareDataService: ShareService,
		private resetAppService: ResetAppService,
		private sessionStorageService: SessionStorageService,
	) {}

	ngOnInit() {
		this.getSelectedCountry();
	}

	onToggle(isOpen: boolean) {
		this.isOpen = isOpen;
		[CLASSES.internal, CLASSES.untouchable].forEach((className) =>
			document.querySelector(SELECTORS.overlay_container).classList.toggle(className, isOpen),
		);
	}

	onClick() {
		document.querySelector(SELECTORS.overlay_container).classList.toggle(CLASSES.internal, !this.isOpen);

		this.gtm.pushEvent(GTM_HEADER_EVENTS.COUNTRY_SELECTOR);
	}

	openConfirmationModal(country: Country) {
		this.modalService
			.openResponsiveDialog({
				component: ConfirmModalComponent,
				data: { page: 'change_country' },
				...this.modalService.getConfig(ConfirmModalComponent.ID),
			})
			.afterClosed()
			.subscribe(this.confirmationModalHandler.bind(this, country));
	}

	private confirmationModalHandler(country: Country, status: boolean): void {
		document.querySelector(SELECTORS.overlay_container).classList.add(CLASSES.untouchable);

		if (status) {
			this.selectedCountry = country;

			this.resetAppService.resetServices({ blindDetection: true, partAvailability: true });
			this.resetAppService.resetApp({ country });
		} else {
			this.matSelect.value = this.selectedCountry;
		}

		this.gtm.pushEvent(GTM_COUNTRY_EVENS.CHANGE_COUNTRY(this.selectedCountry.name));
	}

	private getSelectedCountry() {
		if (this.getCountryCodeFromStorage()) {
			this.selectedCountry = this.getCountryFromConfig(this.getCountryCodeFromStorage());
		}
		// else {
		// TODO: work out why this is an else: This seems like it should be run either way?
		this.shareDataService.getCountry.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((code) => {
			this.selectedCountry = this.getCountryFromConfig(code);
		});
		// }

		if (this.isShop) {
			this.selectedCountry = this.getCountryFromConfig('au');
			this.sessionStorageService.setSession(this.selectedCountry.code, STORAGE_NAMES.zip_country_code);
		}
	}

	private getCountryCodeFromStorage(): string | null {
		return this.sessionStorageService.getSession(STORAGE_NAMES.zip_country_code);
	}

	private getCountryFromConfig(code: string): Country {
		return Countries.filter((country) => country.code === code)[0];
	}
}
